<template>
  <div>
    <v-breadcrumbs class="d-print-none">
      <v-breadcrumbs-item :exact="true" :to="{ name: 'home', params: {} }"
        >Home</v-breadcrumbs-item
      >
      <v-breadcrumbs-divider>/</v-breadcrumbs-divider>
      <v-breadcrumbs-item
        :exact="true"
        :to="{ name: 'processWorksheet', params: {} }"
      >
        Process Worksheet
      </v-breadcrumbs-item>
      <v-breadcrumbs-divider>/</v-breadcrumbs-divider>
      <v-breadcrumbs-item :to="{ name: 'processWorksheetView', params: {} }">
        Worksheet Print Label
      </v-breadcrumbs-item>
    </v-breadcrumbs>

    <div class="d-print-none">
      <div class="d-flex justify-center align-center flex-wrap my-2">
        <!-- One button that prints, rather than two that ask the operator to
             pick a technology. The phone already knows: a station with a
             printer paired to it prints over Bluetooth, anything else goes
             through the print dialog. It still names its destination, because
             the same screen behaves differently on different devices and that
             should never be a surprise. -->
        <v-btn
          :color="preferBluetooth ? 'indigo' : 'primary'"
          dark
          large
          class="ma-1"
          :loading="btBusy"
          :disabled="btBusy"
          @click="preferBluetooth ? printBluetooth() : print()"
        >
          <v-icon left>
            {{ preferBluetooth ? "mdi-bluetooth" : "mdi-printer" }}
          </v-icon>
          {{ preferBluetooth ? "พิมพ์ (บลูทูธ)" : "พิมพ์ (USB / WiFi)" }}
        </v-btn>
        <!-- The other way out: a paired station occasionally needs A4 or a PDF,
             and printers fail. Quiet, but never absent. -->
        <v-btn
          v-if="btSupported"
          text
          small
          class="ma-1"
          :disabled="btBusy"
          @click="preferBluetooth ? print() : printBluetooth()"
        >
          {{ preferBluetooth ? "ใช้ USB / WiFi แทน" : "ใช้บลูทูธแทน" }}
        </v-btn>
        <v-btn
          v-if="btBusy"
          color="red"
          outlined
          large
          class="ma-1"
          @click="cancelBluetooth()"
        >
          <v-icon left>mdi-stop</v-icon>
          ยกเลิก / Cancel
        </v-btn>
        <!-- Copies is changed far too often to live behind Advanced Settings.
             The bitmap is sent once per label and each copy is a short PRINT
             command, so extra copies cost almost nothing on the wire. -->
        <v-text-field
          v-if="btSupported"
          v-model.number="parameters.copies"
          class="ma-1 flex-grow-0"
          style="max-width: 130px"
          outlined
          dense
          hide-details
          type="number"
          min="1"
          max="20"
          :disabled="btBusy"
          label="สำเนา / Copies"
        />
        <!-- The roll can differ from the one this phone last printed on: a lost
             sticker gets reprinted wherever there is a printer, not necessarily
             the station that made it. Choosing the size was only possible in the
             auto-print panel on the entry screen, which does not exist for a
             worksheet that has already been submitted — so a 100x150 reprint had
             no route at all. Named sizes rather than width/height boxes, because
             a mistyped dimension prints a whole run wrong. -->
        <v-select
          v-if="!btBusy"
          :value="sizeKey"
          :items="sizeItems"
          class="ma-1 flex-grow-0"
          style="max-width: 160px"
          outlined
          dense
          hide-details
          label="ขนาด / Size"
          @change="applySize"
        />
        <!-- Reprinting a few labels after a stall is the moment someone is
             under pressure, so it does not belong behind an "Advanced" panel.
             Hidden while printing, where it is meaningless and would only
             crowd the row next to Cancel. -->
        <v-text-field
          v-if="!btBusy"
          v-model="filterTerm"
          class="ma-1 flex-grow-0"
          style="max-width: 150px"
          outlined
          dense
          hide-details
          clearable
          placeholder="all"
          label="เฉพาะข้อ / Only #"
        />
      </div>
      <div v-if="btSupported && btProgress.total" class="mx-auto mb-2" style="max-width: 480px">
        <v-progress-linear
          :value="(btProgress.done / btProgress.total) * 100"
          height="6"
          rounded
          color="indigo"
        />
      </div>
      <div v-if="btStatus" class="text-center text-caption grey--text mb-2">
        {{ btStatus }}
      </div>

      <!-- Every step of a Bluetooth print, on screen. The failures here happen
           on a phone on the factory floor with no console attached, and a
           one-line status was not enough to tell a dead button from a dropped
           connection from a printer that ignored the data. -->
      <v-card v-if="btLog.length" outlined class="mx-auto mb-3" max-width="480">
        <v-card-text class="py-2">
          <div
            v-for="(line, i) in btLog"
            :key="i"
            class="text-caption"
            style="font-family: monospace; white-space: pre-wrap"
          >
            {{ line }}
          </div>
        </v-card-text>
      </v-card>
      <v-expansion-panels class="mb-5" flat>
        <v-expansion-panel>
          <v-expansion-panel-header>Advanced Print Settings</v-expansion-panel-header>
          <v-expansion-panel-content>
      <page-params
        pageName="worksheetPrintLabel"
        vuexName="labelPrintParameters"
        :parameters="settings"
        @onLoad="manualPresetLoaded = true"
      />
      <div class="text-caption grey--text mx-2 mb-2">
        Brand name, commodity override and extra fields are remembered per
        product automatically: whatever you print with becomes the default the
        next time "{{ processWorksheet.productName || 'this product' }}" labels are printed.
      </div>
      <v-divider />
      <v-subheader>Label Print Settings</v-subheader>
      <div>
        <div class="d-flex">
          <v-text-field label="Brand Name" class="mx-2"
            outlined
            dense v-model="parameters.brandName"/>
          <v-text-field label="Commodity Override" class="mx-2"
            outlined
            dense v-model="parameters.commodityOverride"/>
          <v-btn class="ml-2" @click="addStickerField()">Add Field</v-btn>
        </div>
      </div>
      <div v-for="(field, index) in parameters.optionalFields" :key="index">
        <div class="d-flex">
          <v-text-field label="Field Label" class="mx-2"
              outlined
              dense v-model="field.label"/>
          <v-text-field label="Field Value" class="mx-2"
              outlined
              dense v-model="field.value"/>
          <v-btn @click="deleteField(index)" icon><v-icon>mdi-delete-outline</v-icon></v-btn>
        </div>
      </div>
      <div class="d-flex">
        <v-text-field
          class="mx-2"
          suffix="mm"
          outlined
          dense
          label="Label Width"
          v-model="parameters.labelWidth"
          type="number"
        ></v-text-field>
        <v-text-field
          class="mx-2"
          suffix="mm"
          outlined
          dense
          label="Label Height"
          v-model="parameters.labelHeight"
          type="number"
        ></v-text-field>
        <v-switch
          class="mx-2 mt-1"
          dense
          label="Rotate 90°"
          hint="Match printers that feed labels portrait"
          persistent-hint
          v-model="parameters.rotate"
        ></v-switch>
      </div>
      <div class="d-flex">
        <v-text-field
          class="mx-2"
          suffix="px"
          outlined
          dense
          label="Left"
          v-model="parameters.left"
          type="number"
        ></v-text-field>
        <v-text-field
          class="mx-2"
          suffix="px"
          outlined
          dense
          label="Top"
          v-model="parameters.top"
          type="number"
        ></v-text-field>
        <v-text-field
          class="mx-2"
          suffix="px"
          outlined
          dense
          label="Right"
          v-model="parameters.right"
          type="number"
        ></v-text-field>
        <v-text-field
          class="mx-2"
          suffix="px"
          outlined
          dense
          label="Bottom"
          v-model="parameters.bottom"
          type="number"
        ></v-text-field>
      </div>
      <div class="d-flex">
        <v-text-field
          class="mx-2"
          suffix="pt"
          outlined
          dense
          label="Font Size"
          v-model="parameters.fontSize"
          type="number"
        ></v-text-field>
        <v-text-field
          class="mx-2"
          suffix="pt"
          outlined
          dense
          label="Header Font Size"
          v-model="parameters.headerFontSize"
          type="number"
        ></v-text-field>
        <v-text-field
          class="mx-2"
          suffix="pt"
          outlined
          dense
          label="Footer Font Size"
          v-model="parameters.footerFontSize"
          type="number"
        ></v-text-field>
        <v-text-field
          class="mx-2"
          suffix="pt"
          outlined
          dense
          label="QR Code Size"
          v-model="parameters.qrSize"
          type="number"
        ></v-text-field>
      </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <v-divider class="mb-5"/>
    </div>
    <div class="sticker-container">
      <div class="label-page" v-for="entry in filteredEntries" :key="entry.id">
        <label-sticker
          :entry="entry"
          :worksheet="processWorksheet"
          :parameters="parameters"
          :qr-data-url="qrDataUrls[entry.id] || ''"
          :lot-index="entryIndex(entry)"
          :commodity-name="commodityName"
          :product-info="productInfo"
          :company-name="companyName"
          :company-address="companyAddress"
          :company-phone="companyPhone"
        />
      </div>
    </div>
  </div>
</template>

<script>
import QRCode from "qrcode";
import WorksheetPrint from "@/components/mixins/WorksheetPrint.js";
import { mapActions, mapGetters } from "vuex";
import btPrinter from "@/utils/btPrinter.js";
import {
  needsRotate90,
  printQuality,
  LABEL_SIZES,
  sizeKeyOf,
  saveDeviceConfig,
} from "@/utils/labelDeviceConfig.js";
import {
  stickerToTsplBytes,
  clampCopies,
} from "@/utils/tsplLabel.js";
const PageParams = () => import("@/components/common/PageParams.vue");
const LabelSticker = () =>
  import("@/components/processWorksheet/LabelSticker.vue");

const PAGE_STYLE_ID = "worksheet-label-print-page-style";

// Printer hardware config is per device (each station/phone talks to its
// own printer with its own label roll) — persisted in localStorage, unlike
// the per-product content fields which live server-side
const DEVICE_CONFIG_KEY = "labelPrintDeviceConfig";
// Pause after each label, multiplied by the number of copies, so the printer is
// never sent a new job while it is still printing the last one.
const LABEL_GAP_KEY = "labelPrintGapMs";
// A 50mm label prints in about a third of a second at this printer's 150mm/s,
// and copies of one label are produced by the printer itself back to back. 1200
// was guesswork from before that was known, and it made every label wait
// seconds longer than the printer needed.
const DEFAULT_LABEL_GAP_MS = 500;
const DEVICE_CONFIG_FIELDS = [
  "labelWidth",
  "labelHeight",
  "rotate",
  "left",
  "right",
  "top",
  "bottom",
  "qrSize",
  "copies",
  "fontSize",
  "headerFontSize",
  "footerFontSize",
];

export default {
  mixins: [WorksheetPrint],
  data() {
    return {
      autoPrinted: false,
      qrDataUrls: {},
      productPreset: null,
      manualPresetLoaded: false,
      btSupported: btPrinter.isSupported(),
      btBusy: false,
      btStatus: "",
      btProgress: { done: 0, total: 0 },
      btCancelled: false,
      // Index (0-based) of the label to Resume from after a cancel / paper-out
      // stop; null when there's nothing to resume.
      btLog: [],
    };
  },
  components: {
    PageParams,
    LabelSticker,
  },
  computed: {
    ...mapGetters("labelPrintParameters", ["parameters"]),

    settings() {
      return this.parameters;
    },
    // Which button is the main one. Same signal used to decide whether the
    // print dialog should open on arrival, so the page is consistent about what
    // kind of station it thinks it is on.
    preferBluetooth() {
      return this.btSupported && btPrinter.hasRememberedPrinter();
    },
    sizeItems() {
      return LABEL_SIZES.map((size) => ({ text: size.title, value: size.key }));
    },
    sizeKey() {
      return sizeKeyOf(this.parameters);
    },
    // How long to wait after a label before sending the next job, per copy.
    // The printer reports nothing back, so this is a timed guess at how long it
    // needs to feed and print; sending early means the bytes land on a full
    // buffer and are silently dropped. Tunable per device with ?btGap=1200
    // because it depends on the printer, the label size and the print speed.
    labelGapMs() {
      const q = Number(this.$route.query.btGap);
      if (q >= 0) {
        try {
          localStorage.setItem(LABEL_GAP_KEY, String(q));
        } catch (e) {
          // storage unavailable — the query param still applies this time
        }
        return q;
      }
      try {
        const saved = Number(localStorage.getItem(LABEL_GAP_KEY));
        if (saved > 0) {
          return saved;
        }
      } catch (e) {
        // fall through to the default
      }
      return DEFAULT_LABEL_GAP_MS;
    },
    // One label page per sticker, sized to the label media. Thermal
    // label drivers usually report wide labels as portrait paper
    // (50mm x 90mm), so by default the sticker is rotated 90deg to
    // fill the page; toggle off for drivers with landscape media.
    pageCss() {
      const width = Number(this.parameters.labelWidth) || 90;
      const height = Number(this.parameters.labelHeight) || 50;
      // A layout wider than the print head has to feed short-edge-first, so it
      // is rotated whether it goes out over Bluetooth or through the print
      // dialog. Without this the 100x150 preset came out correct on Bluetooth
      // and wrong on a plugged-in printer, for no reason the operator could see.
      const rotated = this.parameters.rotate || needsRotate90(this.parameters);
      // Strip app-shell spacing (body, v-main, container) so the label
      // starts at the exact top-left of the page — any offset overflows
      // the tiny page box and makes Chrome shrink the whole label.
      // min-width: Bootstrap's print stylesheet forces body/.container to
      // min-width 992px !important, which makes Chrome shrink the whole
      // 90mm label page to its 2/3 clamp — undo it here.
      const reset = `
        body {
          margin: 0 !important;
          min-width: 0 !important;
        }
        .v-main { padding: 0 !important; }
        .v-main .container {
          padding: 0 !important;
          margin: 0 !important;
          max-width: 100% !important;
          min-width: 0 !important;
        }`;
      // One label per page — gap-sensing thermal printers advance to the
      // next label by the media gap, so each sticker must be its own page
      if (rotated) {
        return `@media print {
          @page { size: ${height}mm ${width}mm; margin: 0; }
          ${reset}
          div.label-page { width: ${height}mm; height: ${width}mm; position: relative; }
          div.label-page > div.sticker {
            position: absolute;
            top: 0;
            left: 0;
            transform: rotate(90deg) translateY(-100%);
            transform-origin: top left;
          }
        }`;
      } else {
        return `@media print {
          @page { size: ${width}mm ${height}mm; margin: 0; }
          ${reset}
          div.label-page { width: ${width}mm; height: ${height}mm; }
        }`;
      }
    },
    // Identifies the exact set of labels on screen. The resume point is a
    // position in that list, so it is only valid while this is unchanged.
    entrySignature() {
      return this.filteredEntries.map((e) => e.id).join(",");
    },
    filteredEntries(){
      let entries = this.groupedEntries;

      if(this.filterTerm){
        const matchIndexes = this.parseFilterTerms(this.filterTerm);
        entries = entries.filter( (e, index) => {
          return matchIndexes.includes(index+1);
        })
      }

      return entries;
    }
  },
  watch: {
    pageCss: {
      immediate: true,
      handler(css) {
        this.injectPageStyle(css);
      },
    },
    // Changing which labels are on screen invalidates whatever the last batch
    // reported, since the numbers it named refer to the old selection.
    filterTerm() {
      this.btStatus = "";
    },
    // Remember this device's printer setup across visits/reloads
    parameters: {
      deep: true,
      handler(params) {
        const config = {};
        DEVICE_CONFIG_FIELDS.forEach((key) => {
          config[key] = params[key];
        });
        try {
          localStorage.setItem(DEVICE_CONFIG_KEY, JSON.stringify(config));
        } catch (error) {
          // Storage unavailable — settings just won't persist
        }
      },
    },
  },
  methods: {
    ...mapActions("navigation", ["hideDrawer"]),
    ...mapActions("labelPrintParameters", ["updateParameters"]),
    ...mapActions("pageParameters", [
      "fetchPageParameters",
      "createPageParameters",
      "updatePageParameters",
    ]),

    // Same per-device record the entry screen writes, so a station set up for
    // one roll prints that roll from either screen.
    applySize(key) {
      const size = LABEL_SIZES.find((s) => s.key === key);
      if (!size) {
        return;
      }
      this.updateParameters({
        labelWidth: size.labelWidth,
        labelHeight: size.labelHeight,
      });
      saveDeviceConfig(this.parameters);
    },
    injectPageStyle(css) {
      let styleEl = document.getElementById(PAGE_STYLE_ID);
      if (!styleEl) {
        styleEl = document.createElement("style");
        styleEl.id = PAGE_STYLE_ID;
        document.head.appendChild(styleEl);
      }
      styleEl.textContent = css;
    },
    async print() {
      await this.hideDrawer();
      await this.saveProductPreset();
      window.print();
    },
    // Direct-to-printer path for BLE thermal printers (Gprinter AS240 etc.):
    // rasterise each sticker to TSPL and stream it over Web Bluetooth — no
    // browser print dialog, no driver, exact 90x50 every time. Starts a fresh
    // batch from the first label.
    async printBluetooth() {
      if (this.btBusy) {
        return;
      }
      // The print button always prints, from the first label. It used to open a
      // "batch not finished" dialog whenever a resume point existed — and since
      // that resume point was restored from localStorage on every load, one
      // interrupted batch left the button apparently dead forever afterwards.
      // Reprinting from the top is recoverable; a button that does nothing is not.
      await this.startBluetoothBatch();
    },
    async startBluetoothBatch() {
      this.btLog = [];
      this.logBt("tapped print");
      await this.hideDrawer();
      await this.saveProductPreset();
      await this.$nextTick();
      await this.runBluetoothBatch(0);
    },
    // A stopped batch says which numbers to print again, because that is the
    // only recovery now: a Resume button skipped a label in testing, and the
    // printer running one job behind means we can never be sure the label we
    // last SENT actually came out. Reprinting one sticker twice is thrown away;
    // a gap is a bundle that ships unlabelled.
    stopAt(index, reason) {
      const entry = this.filteredEntries[index];
      const from = entry ? this.entryIndex(entry) : null;
      const last = this.filteredEntries[this.filteredEntries.length - 1];
      const to = last ? this.entryIndex(last) : null;
      this.btStatus =
        from && to
          ? `${reason} — พิมพ์ต่อ: ใส่ ${from}-${to} ในช่อง "เฉพาะข้อ" / to finish, type ${from}-${to} in "Only #"`
          : reason;
    },
    cancelBluetooth() {
      this.btCancelled = true;
    },
    // Every step of a Bluetooth print, recorded on screen. There is no console
    // on a phone on the factory floor, and the difference between a dead
    // button, a dropped link and a printer that ignored the data is invisible
    // without this.
    logBt(line) {
      this.btLog.push(line);
      if (this.btLog.length > 60) {
        this.btLog.shift();
      }
    },
    // Drop the GATT link so the printer resumes advertising and is findable
    // again. Safe to call when nothing is connected.
    releasePrinter() {
      // NEVER mid-batch. pagehide fires when a phone dims or backgrounds the
      // tab during a long print; disconnecting there kills the in-flight write
      // and strands the batch part-way through.
      if (this.btBusy) {
        return;
      }
      try {
        btPrinter.disconnect();
      } catch (error) {
        // Already gone — nothing to release.
      }
    },
    // Print labels [startIndex .. end] one at a time with live progress.
    // Paced + last-chunk-acked writes keep the printer's small buffer from
    // overrunning on big (50+) batches; on a stall (paper-out) or Cancel it
    // stops cleanly and records where to Resume.
    async runBluetoothBatch(startIndex) {
      this.btBusy = true;
      this.btCancelled = false;
      try {
        if (!btPrinter.isConnected()) {
          this.btStatus = "กำลังเชื่อมต่อเครื่องพิมพ์ / Connecting…";
          this.logBt("connecting…");
          const name = await btPrinter.connect();
          this.logBt(`connected: ${name} [${btPrinter.writeMode()}]`);
          btPrinter
            .discoveredMap()
            .forEach((entry) => this.logBt(`  ${entry}`));
        } else {
          this.logBt(`already connected [${btPrinter.writeMode()}]`);
        }

        // Stickers render in filteredEntries order in the DOM.
        const stickers = this.$el.querySelectorAll(".sticker");
        const total = stickers.length;
        this.logBt(`stickers on page: ${total}, gap ${this.labelGapMs}ms/copy`);
        if (!total) {
          throw new Error("No labels to print.");
        }
        this.btProgress = { done: startIndex, total };

        const widthMm = Number(this.parameters.labelWidth) || 90;
        const heightMm = Number(this.parameters.labelHeight) || 50;

        const copies = clampCopies(this.parameters.copies);

        for (let i = startIndex; i < total; i++) {
          if (this.btCancelled) {
            this.stopAt(i, "ยกเลิกแล้ว / Cancelled");
            return;
          }
          this.btStatus =
            `กำลังพิมพ์ / Printing ${i + 1}/${total}` +
            (copies > 1 ? ` ×${copies}` : "") +
            "…";
          try {
            // One complete job per label: bitmap + PRINT 1,<copies>. The printer
            // makes the duplicates itself — one acked send per label, nothing
            // split. This is the pattern that printed big batches reliably.
            const bytes = await stickerToTsplBytes(stickers[i], {
              widthMm,
              heightMm,
              dpi: 203,
              gapMm: 2,
              direction: this.parameters.rotate ? 1 : 0,
              rotate90: needsRotate90(this.parameters),
              ...printQuality(),
              copies,
            });
            await btPrinter.write(bytes, {
              shouldAbort: () => this.btCancelled,
            });
            this.logBt(`label ${i + 1}: sent ${bytes.length} bytes OK`);
          } catch (labelError) {
            // Cancelled part-way through sending this label: the printer has
            // half a job, so the label is not counted as printed and the whole
            // of it is reprinted if they continue.
            if (labelError && labelError.cancelled) {
              this.logBt(`label ${i + 1}: cancelled mid-send`);
              this.stopAt(i, "ยกเลิกแล้ว / Cancelled");
              return;
            }
            const m =
              labelError && labelError.message
                ? labelError.message
                : String(labelError);
            this.logBt(
              `label ${i + 1}: FAILED ${
                (labelError && labelError.name) || ""
              } ${m}`
            );
            this.logBt(`connected now: ${btPrinter.isConnected()}`);
            this.stopAt(i, `หยุดที่ / Stopped: ${m}`);
            return;
          }
          this.btProgress = { done: i + 1, total };
          // Small pace between labels so the printer keeps up on long runs.
          // Wait for the printer to physically finish before sending the next
          // job. There is no status line back from it, so this is timed: a
          // 90×50 label takes roughly a second to feed and print, and N copies
          // take N times as long. 300ms flat was enough while copies was 1 and
          // far too short at 2 — the next 36KB arrived while the printer was
          // still printing, hit a full buffer, and was silently discarded.
          await new Promise((r) => setTimeout(r, this.labelGapMs * copies));
        }
        this.btStatus =
          `พิมพ์เสร็จ / Printed ${total} label(s)` +
          (copies > 1 ? ` ×${copies}` : "") +
          `. [${btPrinter.writeMode()}]`;
        this.logBt(`finished: sent ${total} label(s) ×${copies}`);
      } catch (error) {
        this.btStatus = "";
        const msg = error && error.message ? error.message : String(error);
        this.logBt(`ERROR ${(error && error.name) || ""}: ${msg}`);
        // Chrome throws NotFoundError both when the operator dismisses the
        // chooser and when they dismiss it *because the printer wasn't listed*.
        // The second is the common case and has a concrete remedy: a BLE printer
        // stops advertising while it thinks it is connected, and waking it makes
        // it appear again. Saying so beats an operator concluding it's broken.
        if (/NotFound/i.test(error && error.name) || /cancel/i.test(msg)) {
          this.btStatus =
            "ไม่พบเครื่องพิมพ์? กดปุ่ม FEED หรือปิด-เปิดเครื่องพิมพ์ แล้วลองอีกครั้ง / " +
            "Printer not listed? Press FEED or power-cycle the printer, then try again.";
        } else {
          this.addErrorMessage(`Bluetooth print failed: ${msg}`);
        }
      } finally {
        this.btBusy = false;
      }
    },
    // High-res QR bitmaps rendered once per entry; the <img> scales them
    // to the configured size on screen and in print
    async buildQrDataUrls() {
      const urls = {};
      for (const entry of this.groupedEntries) {
        urls[entry.id] = await QRCode.toDataURL(this.getQrCodeUrl(entry), {
          margin: 0,
          width: 600,
          errorCorrectionLevel: "M",
        });
      }
      this.qrDataUrls = urls;
    },
    // Label users just want the printer dialog — pop it as soon as the
    // labels have rendered, no manual settings step. But a phone that has
    // already chosen a Bluetooth printer prints with the Bluetooth button, and
    // for it this dialog is only something to dismiss before every single
    // print, so it is not opened there.
    autoPrint() {
      if (this.autoPrinted || btPrinter.hasRememberedPrinter()) {
        return;
      }
      this.autoPrinted = true;
      this.$nextTick(() => {
        setTimeout(() => this.print(), 800);
      });
    },
    async setInstance() {
      await this.loadWorksheetInstance();
      await this.applyProductPreset();
      await this.buildQrDataUrls();
      this.autoPrint();
    },
    // Products differ in which fields their labels need (e.g. brand name
    // only for some). The last-printed brand/fields are remembered per
    // product (as a saved preset named after the product) and restored here.
    async applyProductPreset() {
      const productName = this.processWorksheet.productName;
      if (!productName) {
        return;
      }
      try {
        const response = await this.fetchPageParameters({
          page: 0,
          size: 200,  // page_parameters: 178 rows on production
          pageName: "worksheetPrintLabel",
          vuexName: "labelPrintParameters",
        });
        const presets = response && response.content ? response.content : [];
        this.productPreset =
          presets.find(
            (p) =>
              p.name &&
              p.name.trim().toLowerCase() === productName.trim().toLowerCase()
          ) || null;
        if (this.productPreset && this.productPreset.parameters) {
          this.updateParameters(JSON.parse(this.productPreset.parameters));
        }
      } catch (error) {
        // No preset for this product — print with the current settings
      }
    },
    // Auto-remember: whatever content the user prints with becomes the
    // product's default for next time — no manual save step
    async saveProductPreset() {
      const productName = this.processWorksheet.productName;
      if (!productName) {
        return;
      }
      // Special case: the user explicitly loaded a saved setting for this
      // print — treat it as a one-off, keep the product's own memory
      if (this.manualPresetLoaded) {
        return;
      }
      const content = JSON.stringify({
        brandName: this.parameters.brandName || null,
        commodityOverride: this.parameters.commodityOverride || null,
        optionalFields: this.parameters.optionalFields || [],
      });
      try {
        if (this.productPreset) {
          if (this.productPreset.parameters === content) {
            return; // unchanged — skip the round-trip
          }
          await this.updatePageParameters({
            ...this.productPreset,
            name: productName,
            parameters: content,
          });
          this.productPreset = { ...this.productPreset, parameters: content };
        } else {
          const created = await this.createPageParameters({
            name: productName,
            page_name: "worksheetPrintLabel",
            vuex_name: "labelPrintParameters",
            owner_only: false,
            parameters: content,
          });
          this.productPreset = created || { name: productName, parameters: content };
        }
      } catch (error) {
        // Remembering is best-effort — never block printing on it
      }
    },
  },
  async created() {
    this.id = this.$route.params.id;
    // Restore this device's printer setup (label size, rotation, fonts)
    try {
      const deviceConfig = JSON.parse(localStorage.getItem(DEVICE_CONFIG_KEY));
      if (deviceConfig) {
        // stale field from the removed 3-up feature — ignore if present
        delete deviceConfig.labelsPerPage;
        // A config saved before a field existed has no key for it. Assigning
        // that undefined would wipe the store's default (e.g. copies: 4) and
        // silently drop every device that printed before this release to 1.
        Object.keys(deviceConfig).forEach((key) => {
          if (deviceConfig[key] === undefined || deviceConfig[key] === null) {
            delete deviceConfig[key];
          }
        });
        this.updateParameters(deviceConfig);
      }
    } catch (error) {
      // Corrupt/missing device config — fall back to defaults
    }
    // Allow deep-linking a single entry's label, e.g. ?entries=3
    if (this.$route.query.entries) {
      this.filterTerm = String(this.$route.query.entries);
    }
    // The store module persists printer config (label size, rotation, fonts)
    // across visits, but worksheet-specific content must NOT carry onto the
    // next worksheet's labels — clear it before loading this one. setInstance()
    // re-sets brandName for LME products below.
    this.updateParameters({ brandName: null, commodityOverride: null, optionalFields: [] });
    this.lmeBrands = await this.getLmeBrandProducts();
    this.setInstance();
    await this.$nextTick();
    window.addEventListener("pagehide", this.releasePrinter);
    this.$emit("showParent", false);
  },
  beforeDestroy() {
    // Stop a batch that is still running. The loop holds the label elements it
    // rasterises, and once this page is gone they are detached from the
    // document — every remaining label would come out a blank sticker. The
    // in-flight label still finishes; the rest are simply not printed. There is
    // no resuming afterwards (the page is gone), so the way back is to reprint
    // the remaining numbers with the entry filter.
    this.btCancelled = true;
    window.removeEventListener("pagehide", this.releasePrinter);
    // Hand the printer back when leaving the page. A BLE printer stops
    // advertising while it believes it is connected, so a link left open by a
    // closed tab makes it invisible in the chooser next time — which looks to
    // the operator like a dead printer that only a power-cycle revives.
    this.releasePrinter();
    const styleEl = document.getElementById(PAGE_STYLE_ID);
    if (styleEl) {
      styleEl.remove();
    }
  },
};
</script>
<style scoped>
/* The sticker itself (layout, watermark, fonts) lives in LabelSticker.vue.
   What remains here is only the page each sticker is printed on. */

@media print {

  div.sticker-container {
    margin: 0;
    padding: 0;
  }

  /* One label media page per sticker; size (and rotation) comes from
     the dynamically injected page style */
  div.label-page {
    position: relative;
    overflow: hidden;
    break-inside: avoid;
    page-break-after: always;
    page-break-inside: avoid;
  }

  div.label-page:last-child {
    page-break-after: auto;
  }
}
</style>
